import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { IResetPassword } from "@shared/interfaces/user.interface";
import { UserPasswordService } from "@shared/services/user-password.service";
import Validation from "@shared/utils/helpers/validators/form-field-custom.validators";
import { Subject, finalize, first } from "rxjs";

@Component({
  selector: "app-reset-password-form",
  templateUrl: "./reset-password-form.component.html",
  styleUrls: ["./reset-password-form.component.scss"],
})
export class ResetPasswordFormComponent implements OnInit, OnDestroy {
  @Input() code?: string;

  @Output() resetPasswordFormSubmit: EventEmitter<void> = new EventEmitter<void>();
  @Output() backClick: EventEmitter<void> = new EventEmitter<void>();
  @Input() isModal = false;
  public emailForm = this.fb.nonNullable.group({
    email: [
      "",
      [
        Validators.required,
        Validators.email,
        Validators.maxLength(255),
        Validators.pattern(/^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/),
      ],
    ],
  });
  public codeForm = this.fb.nonNullable.group(
    {
      code: ["", [Validators.required]],
      password: ["", [Validators.required, Validators.maxLength(255), Validators.pattern(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/)]],
      confirmPassword: ["", [Validators.required, Validators.maxLength(255)]],
    },
    {
      validators: [Validation.match("password", "confirmPassword")],
    }
  );

  public hasCode = false;
  public showPassword = false;
  public showConfirmPassword = false;
  public isEmailError = false;
  public isCodeError = false;

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private userPasswordService: UserPasswordService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {}

  /**
   * Initializes the hasCode variable and code field
   */
  ngOnInit(): void {
    if (this.code) {
      this.hasCode = true;

      this.codeForm.patchValue({
        code: this.code,
      });
    }
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Handles back navigation depending on context
   */
  public goBack(): void {
    if (this.isModal) {
      this.backClick.emit();
    } else {
      this.router.navigate(["/login"]);
    }
  }

  /**
   * Handles form submission for email or password reset flow
   */
  public onSubmit(): void {
    if (!this.hasCode) {
      if (this.emailForm.invalid) {
        this.emailForm.markAllAsTouched();
        return;
      }
      this.emailForm.disable();
      this.userPasswordService
        .forgotPassword(this.emailForm.controls.email.value)
        .pipe(
          first(),
          finalize(() => this.emailForm.enable())
        )
        .subscribe({
          next: () => {
            this.isEmailError = false;
            this.snackBar.open(`✅ ${this.translateService.instant("page.reset-password.emailSuccess")}`, "OK", {
              duration: 5000,
            });
            this.hasCode = true;
          },
          error: (err) => {
            if (err.status === 400) {
              this.isEmailError = true;
            } else {
              this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
                duration: 5000,
              });
            }
          },
        });
    } else {
      if (this.codeForm.invalid) {
        this.codeForm.markAllAsTouched();
        return;
      }
      this.codeForm.disable();
      const resetPassword: IResetPassword = {
        token: this.codeForm.controls.code.value,
        password: this.codeForm.controls.password.value,
      };
      this.userPasswordService
        .resetPassword(resetPassword)
        .pipe(
          first(),
          finalize(() => this.codeForm.enable())
        )
        .subscribe({
          next: () => {
            this.isCodeError = false;
            this.snackBar.open(`✅ ${this.translateService.instant("page.reset-password.resetSuccess")}`, "OK", {
              duration: 5000,
            });
            this.resetPasswordFormSubmit.emit();
          },
          error: () => {
            this.isCodeError = true;
          },
        });
    }
  }
}
